import { Alert, Autocomplete, Box, Button, FormControl, FormControlLabel, FormLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, Stack, TextField, Typography } from '@mui/material'
import { t } from 'i18next'
import React, { useEffect, useState } from 'react'
import { schedules, scheduleType, TSchedule } from '../../shared/types/schedule.type'
import { weekDays } from '../../enums/days.enums'
import dayjs from 'dayjs'
import { useAuth } from '../../hooks/useAuth'
import { isEmpty, sortBy, toNumber } from 'lodash'
import timezones from 'timezones-list';
import { AnimatePresence, motion } from 'framer-motion';

type StableScheduleProps = {
    schedules: Partial<schedules>[],
    onScheduleChange: (item: any) => void
}
const initialObject: Partial<schedules> = {
    day_of_week: [1], ///First day of week by default
    hour: 14,
    minute: 0,
    type: 'MONTHLY',
    timezone: dayjs.tz.guess(),
    day_of_month: 1

}
function StableSchedule({ schedules, onScheduleChange }: StableScheduleProps) {

    const [selectedSchedule, setSelectedSchedule] = useState<scheduleType>('LAST_WEEK')
    const { auth: user } = useAuth()
    const options: { title: string, value: scheduleType, hint: string }[] = [
        {
            title: t('stable:settings:weekly'),
            hint: t('notification:schedule:lastWeek'),
            value: 'LAST_WEEK'
        },
        {
            title: t('stable:settings:bimonthly'),
            hint: t('stable:settings:bimonthlyHint'),
            value: 'BI_MONTHLY'
        },
        {
            title: t('stable:settings:monthly'),
            hint: t('stable:settings:monthlyHint'),
            value: 'MONTHLY'
        },
    ]
    useEffect(() => {
        if (isEmpty(schedules)) {
            addNewSchedule(selectedSchedule)
        } else {
            let type = schedules[0].type
            if (type) setSelectedSchedule(type)
        }
    }, [selectedSchedule, schedules])


    const addNewSchedule = (type: scheduleType) => {

        var newSchedule = {
            ...initialObject, type, timezone: user?.timezone ?? dayjs.tz.guess()
        };
        let schedule = [...schedules, newSchedule]


        onScheduleChange(schedule)
    }


    const updateState = (index: number, key: keyof schedules, newValue: any) => {
        let find = schedules[index]
        let _schedules = [...schedules]
        if (find) {
            _schedules[index] = { ...find, [key]: newValue }
            onScheduleChange(_schedules)
        }
    }

    const formatNumber = (num: number) => {
        if (String(num).length === 2) {
            return num
        } else {
            return String(num).padStart(2, '0')
        }
    }

    const timeArray = () => {
        let i: number[] = [0]
        let max = 45
        let step = 15
        while (i[i.length - 1] < max) {

            i.push(i[i.length - 1] + step)
        }
        return i
    }



    return (
        <Box p={3}>
            <Typography variant='subtitle2'>{t('configuration:triggers')}</Typography>

            <Alert severity='info' sx={{ my: 1 }}>
                <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1, type: 'spring' }}
                    transition={{
                        duration: 0.2,
                        delay: 0.3
                    }}
                    exit={{ opacity: 0 }} key={selectedSchedule}>
                    {options.find(option => option.value === selectedSchedule)?.hint}
                </motion.span>
            </Alert>
            <FormControl sx={{ mb: 2 }}>
                <RadioGroup
                    aria-labelledby="demo-radio-buttons-group-label"
                    name="radio-buttons-group"
                    value={selectedSchedule}
                    onChange={(_, value) => {
                        const typeValue = value as scheduleType;
                        setSelectedSchedule(typeValue);
                        onScheduleChange(schedules.filter((s) => s?.type === value));
                    }}
                >
                    {options.map(option => <FormControlLabel
                        value={option.value}
                        key={option.value}
                        control={<Radio

                        />}
                        label={option.title}
                    />)}
                </RadioGroup>
            </FormControl>
            <Stack direction={'column'}>
                <AnimatePresence mode='wait' >

                    {
                        schedules.map((schedule, i) =>
                            <motion.div
                                key={`${selectedSchedule}-${i}`}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{
                                    opacity: 0,
                                    transition: { duration: 0.3 }
                                }}
                                transition={{
                                    type: 'spring',
                                    stiffness: 400,
                                    damping: 25,
                                    duration: 0.1,
                                    delay: i * 0.05 // Subtle stagger effect
                                }}
                                style={{ marginBottom: '16px' }} // Spacing between rows
                            >
                                <Box justifyContent={'space-between'} alignItems={'center'} display={'flex'} gap={1} flex={2}>

                                    {
                                        ['LAST_WEEK', 'MONTHLY'].includes(selectedSchedule) && <FormControl size='small' sx={{ flex: 1 }}>
                                            {
                                                selectedSchedule === 'LAST_WEEK' ? <>
                                                    <InputLabel
                                                        id="demo-simple-select-label">{t('settings.downloadingDays.unit')}</InputLabel>
                                                    <Select
                                                        labelId="demo-simple-select-label"
                                                        id="demo-simple-select"
                                                        value={schedule.day_of_week}
                                                        onChange={(e) => {
                                                            updateState(i, 'day_of_week', e.target.value)
                                                        }}
                                                        label={t('settings.downloadingDays.unit')}
                                                    >

                                                        {weekDays.map((item) =>
                                                            <MenuItem value={item.value}>{t(item.key)}</MenuItem>
                                                        )}
                                                    </Select>
                                                </> : <>
                                                    <InputLabel
                                                        id="demo-simple-select-label">{t('settings:downloadingDays:daysOfMonth')}</InputLabel>
                                                    <Select
                                                        labelId={`day-month-label-${i}`}
                                                        value={schedule.day_of_month}
                                                        onChange={(e) => updateState(i, 'day_of_month', e.target.value)}
                                                        label={t('settings:downloadingDays:daysOfMonth')}
                                                        MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }} // Better Styling: Keeps the list from covering the whole screen
                                                    >
                                                        {[...Array(31)].map((_, index) => (
                                                            <MenuItem key={index + 1} value={index + 1}>
                                                                {index + 1}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </>}

                                        </FormControl>
                                    }

                                    <FormControl size='small' sx={{ flex: 1 }}>
                                        <InputLabel
                                            id="demo-simple-select-label">{t('notification:field:hour')}</InputLabel>
                                        <Select

                                            labelId="demo-simple-select-label"
                                            id="demo-simple-select"

                                            value={schedule.hour}
                                            onChange={(e) => updateState(i, 'hour', toNumber(e.target.value))}

                                            label={t('notification:field:hour')}>

                                            {[...Array(24)].map((item, i) =>
                                                <MenuItem key={i}
                                                    value={i + 1}>{i === 23 ? '00' : formatNumber(i + 1)}</MenuItem>
                                            )}
                                        </Select>


                                    </FormControl>

                                    <FormControl size='small' sx={{ flex: 1 }}>
                                        <InputLabel
                                            id="demo-simple-select-label">{t('notification:field:minutes')}</InputLabel>
                                        <Select

                                            labelId="demo-simple-select-label"
                                            id="demo-simple-select"

                                            value={schedule.minute}
                                            onChange={(e) => updateState(i, 'minute', toNumber(e.target.value))}
                                            label={t('notification:field:minutes')}>

                                            {timeArray().map((item, i) =>
                                                <MenuItem key={i} value={item}>{formatNumber(item)}</MenuItem>
                                            )}
                                        </Select>


                                    </FormControl>
                                    <FormControl size='small' sx={{ flex: 2 }}>
                                        <Autocomplete
                                            size='small'
                                            disablePortal
                                            id="combo-box-demo"
                                            value={timezones.find((e) => e.tzCode === schedule?.timezone) ?? undefined}
                                            options={sortBy(timezones, (item) => item.tzCode)}
                                            getOptionKey={tz => tz.tzCode}
                                            onChange={(_, value) => updateState(i, 'timezone', value?.tzCode)}
                                            getOptionLabel={tz => tz.tzCode}
                                            renderInput={(params) => <TextField {...params}
                                                label={t('notification:field:timezone')} />}
                                        />
                                    </FormControl>
                                </Box>
                            </motion.div>
                        )
                    }
                </AnimatePresence>
            </Stack>

        </Box>
    )
}

export default StableSchedule